import { buildNodeGrid } from "../routing/autoPipeRouting/buildNodeGrid.logic";
const RESET_NAMES = true;
const DEBUG = false;
export const calculateDrammenSykehusRoomNumbers = ({ roomsOnFloor, doors = [], floorNumber = 0, }) => {
    const graph = buildDrammenSykehusRoomNumberingGrid(roomsOnFloor, floorNumber);
    const adj = buildDrammenSykehusRoomNumberingAdjacency(graph, roomsOnFloor, doors);
    const { distMap, penaltyCostMap } = buildDrammenSykehusStaircaseDistanceMap(graph, adj, roomsOnFloor);
    const roomAdjTable = buildDrammenSykehusRoomAdjacencyFromGrid(graph);
    return assignDrammenSykehusRoomNumbers(graph, adj, distMap, penaltyCostMap, roomsOnFloor, doors, roomAdjTable, floorNumber);
};
/**
 * Tolerance (metres) within which two polygon edges are considered colinear
 * and therefore sharing a wall.
 */
const ADJACENCY_TOLERANCE = 0.5;
/**
 * Minimum overlap length (metres) required for two edges to count as
 * a shared wall.  This filters out corner-only "adjacencies".
 */
const MIN_SHARED_WALL_LENGTH = 0.05;
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function polygonArea(polygon) {
    const pts = isClosedRing(polygon) ? polygon.slice(0, -1) : polygon;
    const n = pts.length;
    let area = 0;
    for (let i = 0; i < n; i++) {
        const [x1, y1] = pts[i];
        const [x2, y2] = pts[(i + 1) % n];
        area += x1 * y2 - x2 * y1;
    }
    return Math.abs(area) / 2;
}
function polygonCentroid(polygon) {
    let sx = 0;
    let sy = 0;
    // Polygons are closed rings — skip the duplicate last vertex if present.
    const pts = isClosedRing(polygon) ? polygon.slice(0, -1) : polygon;
    for (const [x, y] of pts) {
        sx += x;
        sy += y;
    }
    return [sx / pts.length, sy / pts.length];
}
function isClosedRing(polygon) {
    if (polygon.length < 2)
        return false;
    const first = polygon[0];
    const last = polygon[polygon.length - 1];
    return Math.abs(first[0] - last[0]) < 1e-9 && Math.abs(first[1] - last[1]) < 1e-9;
}
/** Squared distance from point P to the infinite line defined by segment AB. */
function pointToLineDistanceSq(px, py, ax, ay, bx, by) {
    const dx = bx - ax;
    const dy = by - ay;
    const lenSq = dx * dx + dy * dy;
    if (lenSq < 1e-18) {
        const ex = px - ax;
        const ey = py - ay;
        return ex * ex + ey * ey;
    }
    // Perpendicular distance via cross product
    const cross = (px - ax) * dy - (py - ay) * dx;
    return (cross * cross) / lenSq;
}
/**
 * Returns the signed scalar projection of point P onto segment AB,
 * normalised to [0, 1] (0 = A, 1 = B).
 */
function projectOntoSegmentT(px, py, ax, ay, bx, by) {
    const dx = bx - ax;
    const dy = by - ay;
    const lenSq = dx * dx + dy * dy;
    if (lenSq < 1e-18)
        return 0;
    return ((px - ax) * dx + (py - ay) * dy) / lenSq;
}
/**
 * Given two colinear segments A0→A1 and B0→B1 (all four points lie on the
 * same infinite line), computes the length of their overlap.
 *
 * Both segments are expressed as t-values along the direction of A0→A1.
 * Returns 0 if they don't overlap.
 */
function colinearOverlapLength(a0x, a0y, a1x, a1y, b0x, b0y, b1x, b1y) {
    const lenA = Math.hypot(a1x - a0x, a1y - a0y);
    if (lenA < 1e-9)
        return 0;
    // Project B endpoints onto the A axis
    const tB0 = projectOntoSegmentT(b0x, b0y, a0x, a0y, a1x, a1y);
    const tB1 = projectOntoSegmentT(b1x, b1y, a0x, a0y, a1x, a1y);
    const tMin = Math.max(0, Math.min(tB0, tB1));
    const tMax = Math.min(1, Math.max(tB0, tB1));
    if (tMax <= tMin)
        return 0;
    return (tMax - tMin) * lenA;
}
/**
 * Returns the length of the shared wall between two polygons, or 0 if they
 * are not adjacent within ADJACENCY_TOLERANCE.
 *
 * Two edges are considered to share a wall when:
 *   1. Every point on one edge is within ADJACENCY_TOLERANCE of the infinite
 *      line of the other edge (i.e. they are (nearly) colinear).
 *   2. They overlap by at least MIN_SHARED_WALL_LENGTH along that line.
 */
function sharedWallLength(polyA, polyB) {
    const edgesA = polygonEdges(polyA);
    const edgesB = polygonEdges(polyB);
    let maxOverlap = 0;
    for (const [a0, a1] of edgesA) {
        const [a0x, a0y] = a0;
        const [a1x, a1y] = a1;
        const aLenSq = (a1x - a0x) ** 2 + (a1y - a0y) ** 2;
        if (aLenSq < 1e-9)
            continue;
        for (const [b0, b1] of edgesB) {
            const [b0x, b0y] = b0;
            const [b1x, b1y] = b1;
            const bLenSq = (b1x - b0x) ** 2 + (b1y - b0y) ** 2;
            if (bLenSq < 1e-9)
                continue;
            // Check colinearity: both endpoints of B must be close to the line of A,
            // AND both endpoints of A must be close to the line of B.
            const tolSq = ADJACENCY_TOLERANCE * ADJACENCY_TOLERANCE;
            if (pointToLineDistanceSq(b0x, b0y, a0x, a0y, a1x, a1y) > tolSq ||
                pointToLineDistanceSq(b1x, b1y, a0x, a0y, a1x, a1y) > tolSq) {
                continue;
            }
            const overlap = colinearOverlapLength(a0x, a0y, a1x, a1y, b0x, b0y, b1x, b1y);
            if (overlap > maxOverlap) {
                maxOverlap = overlap;
            }
        }
    }
    return maxOverlap >= MIN_SHARED_WALL_LENGTH ? maxOverlap : 0;
}
function polygonEdges(polygon) {
    const edges = [];
    const n = polygon.length;
    for (let i = 0; i < n - 1; i++) {
        edges.push([polygon[i], polygon[i + 1]]);
    }
    // If the ring is NOT already closed, add the closing edge.
    if (!isClosedRing(polygon)) {
        edges.push([polygon[n - 1], polygon[0]]);
    }
    return edges;
}
export function classifyDrammenSykehusRoom(roomName) {
    if (!roomName)
        return "room";
    const lower = roomName.toLowerCase();
    if (/heis|sjakt/.test(lower))
        return "technical";
    if (/trapp|stair/.test(lower))
        return "staircase";
    if (/forrom/.test(lower))
        return "numbered_corridor";
    if (/sluse|sluice|korridor|corridor|g[åa]ng|gang|vestibyle|nisje/.test(lower))
        return "corridor";
    return "room";
}
// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
/**
 * Builds a graph of room nodes and adjacency edges for one floor.
 *
 * - Each room becomes a node with its centroid position and a type derived
 *   from its name (staircase / corridor / room).
 * - Two rooms are connected by an edge when their polygons share a wall
 *   segment of at least MIN_SHARED_WALL_LENGTH within ADJACENCY_TOLERANCE.
 */
export function buildDrammenSykehusRoomGraph(roomsOnFloor) {
    const nodes = roomsOnFloor.map((room, index) => ({
        index,
        roomNumber: room.roomNumber,
        type: classifyDrammenSykehusRoom(room.roomName),
        centroid: polygonCentroid(room.polygon),
    }));
    const edges = [];
    for (let i = 0; i < roomsOnFloor.length; i++) {
        for (let j = i + 1; j < roomsOnFloor.length; j++) {
            const overlap = sharedWallLength(roomsOnFloor[i].polygon, roomsOnFloor[j].polygon);
            if (overlap > 0) {
                edges.push({ a: i, b: j, sharedWallLength: overlap });
            }
        }
    }
    return { nodes, edges };
}
/**
 * Builds an adjacency table for rooms based on the routing grid.
 *
 * Adjacency criteria:
 * 1. If a node in room A is adjacent (via an edge) to a node in room B.
 * 2. If a node is adjacent to a node in room A AND a node in room B (the node acts as a bridge).
 * 3. If a node itself belongs to multiple rooms (shared boundary).
 */
export function buildDrammenSykehusRoomAdjacencyFromGrid(graph) {
    const roomAdj = new Map();
    const nodesById = new Map(graph.nodes.map((n) => [n.id, n]));
    const addAdjacency = (r1, r2) => {
        if (r1 === r2)
            return;
        if (!roomAdj.has(r1))
            roomAdj.set(r1, new Set());
        if (!roomAdj.has(r2))
            roomAdj.set(r2, new Set());
        roomAdj.get(r1).add(r2);
        roomAdj.get(r2).add(r1);
    };
    // Map each node to the set of rooms it can "see" (either belongs to or is adjacent to)
    const nodeToVisibleRooms = new Map();
    for (const node of graph.nodes) {
        nodeToVisibleRooms.set(node.id, new Set(node.rooms ?? []));
    }
    // Process edges: if an edge connects U and V, then U can see V's rooms and vice-versa
    for (const edge of graph.edges) {
        const roomsU = nodesById.get(edge.from)?.rooms ?? [];
        const roomsV = nodesById.get(edge.to)?.rooms ?? [];
        const setU = nodeToVisibleRooms.get(edge.from);
        const setV = nodeToVisibleRooms.get(edge.to);
        if (setU) {
            for (const r of roomsV)
                setU.add(r);
        }
        if (setV) {
            for (const r of roomsU)
                setV.add(r);
        }
    }
    // For every node, all rooms in its "visible" set are considered adjacent to each other
    for (const rooms of nodeToVisibleRooms.values()) {
        const roomList = Array.from(rooms);
        for (let i = 0; i < roomList.length; i++) {
            for (let j = i + 1; j < roomList.length; j++) {
                addAdjacency(roomList[i], roomList[j]);
            }
        }
    }
    return roomAdj;
}
// ---------------------------------------------------------------------------
// Room numbering grid — delegates directly to buildNodeGrid
// ---------------------------------------------------------------------------
/** The step size (metres) between nodes in the room numbering grid. */
const GRID_RESOLUTION = 0.5;
/**
 * Builds a routing graph covering all supplied room polygons.
 * Delegates directly to buildNodeGrid (the pipe-routing grid algorithm) so
 * that the exact same grid logic is reused without duplication.
 */
export function buildDrammenSykehusRoomNumberingGrid(rooms, floorNumber = 0) {
    const { graph } = buildNodeGrid({
        projectNo: 0,
        floorName: `floor-${floorNumber}`,
        selectedRooms: rooms.map((r) => ({ roomNumber: r.roomNumber, polygon: r.polygon })),
        devicesToConnect: [],
        connectionPoints: [[0, 0, 0]],
        pipeElevationsM: { xDirection: 0, yDirection: 1 },
    }, GRID_RESOLUTION);
    return graph;
}
// ---------------------------------------------------------------------------
// Adjacency with corridor-exempt wall penalty
// ---------------------------------------------------------------------------
const PENALTY_WALL = 1000;
const PENALTY_DOOR_ROOM = 100;
/**
 * Builds an adjacency map for Dijkstra over the room numbering grid.
 *
 * Wall-crossing penalty rules:
 *  - If either room involved in the crossing has a name matching
 *    korridor/corridor/gång/gang/sluse/sluice/trapp/stair → no penalty.
 *  - Otherwise → ROOM_WALL_CROSSING_PENALTY (1000) per crossing.
 *
 * @param graph   The routing graph from buildDrammenSykehusRoomNumberingGrid.
 * @param rooms   The original rooms list (needed to look up roomName by roomNumber).
 */
const DOOR_DISTANCE_THR = 0.5;
const DOOR_PERPENDICULAR_DOT_THR = 0.5; // edge must have |dot with long axis| < this (i.e. within ~60° of perpendicular)
/**
 * Returns true if the edge from nodeA to nodeB crosses through the given door.
 *
 * Checks:
 *  1. Edge direction is roughly perpendicular to the door's long axis.
 *  2. Edge midpoint projected onto the long axis falls within the door's extent.
 *  3. Edge midpoint distance to door center along the short axis < DOOR_DISTANCE_THR.
 */
export function edgeCrossesDrammenSykehusDoor(a, b, door) {
    const pts = door.bbox;
    if (pts.length < 2)
        return false;
    // Find the long axis by checking the two distinct edge directions of the bbox rectangle.
    // Use the first three unique points to get two candidate axes.
    const p0 = pts[0];
    const p1 = pts[1];
    const p2 = pts[2];
    const e1x = p1[0] - p0[0], e1y = p1[1] - p0[1];
    const e2x = p2[0] - p1[0], e2y = p2[1] - p1[1];
    const len1 = Math.hypot(e1x, e1y);
    const len2 = Math.hypot(e2x, e2y);
    if (len1 < 1e-9 || len2 < 1e-9)
        return false;
    // Long axis is the longer of the two edges.
    const [lx, ly, longLen, sx, sy, shortLen] = len1 >= len2
        ? [e1x / len1, e1y / len1, len1, e2x / len2, e2y / len2, len2]
        : [e2x / len2, e2y / len2, len2, e1x / len1, e1y / len1, len1];
    // Door center
    const cx = (p0[0] + pts[Math.floor(pts.length / 2)][0]) / 2;
    const cy = (p0[1] + pts[Math.floor(pts.length / 2)][1]) / 2;
    // Edge direction (normalised)
    const edgeLen = Math.hypot(b[0] - a[0], b[1] - a[1]);
    if (edgeLen < 1e-9)
        return false;
    const edx = (b[0] - a[0]) / edgeLen;
    const edy = (b[1] - a[1]) / edgeLen;
    // 1. Edge must be roughly perpendicular to long axis (|dot| < threshold)
    const dotWithLong = Math.abs(edx * lx + edy * ly);
    if (dotWithLong > DOOR_PERPENDICULAR_DOT_THR)
        return false;
    // Edge midpoint
    const mx = (a[0] + b[0]) / 2;
    const my = (a[1] + b[1]) / 2;
    // 2. Midpoint projected onto long axis must fall within door extent.
    // Project relative to door center; door extends ±longLen/2 along long axis.
    const projLong = (mx - cx) * lx + (my - cy) * ly;
    if (Math.abs(projLong) > longLen / 2)
        return false;
    // 3. Midpoint distance to nearest door face along short axis must be < threshold.
    const projShort = Math.abs((mx - cx) * sx + (my - cy) * sy);
    const distToNearestFace = Math.max(0, projShort - shortLen / 2);
    return distToNearestFace < DOOR_DISTANCE_THR;
}
export function buildDrammenSykehusRoomNumberingAdjacency(graph, rooms, doors = []) {
    const roomTypeMap = new Map(rooms.map((r) => [r.roomNumber, classifyDrammenSykehusRoom(r.roomName)]));
    const getNodeMainType = (nodeRooms) => {
        if (nodeRooms.size === 0)
            return "outside";
        const types = Array.from(nodeRooms).map((r) => roomTypeMap.get(r));
        if (types.includes("staircase"))
            return "staircase";
        if (types.includes("corridor"))
            return "corridor";
        if (types.includes("numbered_corridor"))
            return "numbered_corridor";
        if (types.includes("room"))
            return "room";
        if (types.includes("technical"))
            return "technical";
        return "room";
    };
    const infraTypes = ["staircase", "corridor", "numbered_corridor"];
    const adj = new Map();
    for (const node of graph.nodes)
        adj.set(node.id, []);
    const pos = new Map();
    const roomSets = new Map();
    for (const node of graph.nodes) {
        pos.set(node.id, [node.posM[0], node.posM[1]]);
        roomSets.set(node.id, new Set(node.rooms ?? []));
    }
    for (const edge of graph.edges) {
        const a = pos.get(edge.from);
        const b = pos.get(edge.to);
        const dx = a[0] - b[0], dy = a[1] - b[1];
        // Riser edges (same x-y position, connecting x-node to y-node) get a tiny cost
        // to prevent the algorithm from oscillating between layers and ensure a strict distance gradient.
        // All other edges get the x-y euclidean distance.
        const distM = Math.hypot(dx, dy);
        let cost = distM || 0.0001;
        const roomsA = roomSets.get(edge.from);
        const roomsB = roomSets.get(edge.to);
        const typeA = getNodeMainType(roomsA);
        const typeB = getNodeMainType(roomsB);
        const isWallCrossing = !(roomsA.size > 0 && roomsB.size > 0 && [...roomsA].every((r) => roomsB.has(r)) && roomsA.size === roomsB.size);
        const crossesDoor = isWallCrossing && doors.length > 0 && doors.some((door) => edgeCrossesDrammenSykehusDoor(a, b, door));
        let penalty = 0;
        if (isWallCrossing) {
            const aIsInfra = infraTypes.includes(typeA);
            const bIsInfra = infraTypes.includes(typeB);
            if (crossesDoor) {
                // Door crossings: infra-infra free, corridor-room free, staircase/room-room = PENALTY_DOOR_ROOM
                if (aIsInfra && bIsInfra) {
                    penalty = 0;
                }
                else if ((typeA === "corridor" && typeB === "room") || (typeB === "corridor" && typeA === "room")) {
                    penalty = 0;
                }
                else {
                    penalty = PENALTY_DOOR_ROOM;
                }
            }
            else {
                // Non-door crossings: infra-infra free, everything else = PENALTY_WALL
                penalty = (aIsInfra && bIsInfra) ? 0 : PENALTY_WALL;
            }
        }
        cost += penalty;
        adj.get(edge.from)?.push({ neighbor: edge.to, edgeId: edge.id, cost, distM });
        adj.get(edge.to)?.push({ neighbor: edge.from, edgeId: edge.id, cost, distM });
    }
    return adj;
}
/**
 * Runs Dijkstra from all staircase centroids simultaneously and returns the
 * shortest distance from any staircase to every reachable node.
 *
 * Origins: the node closest to the centroid of each staircase room.
 *
 * Expansion continues into all node types — penalties in the adjacency map
 * discourage but do not block paths through ordinary rooms.
 */
export function buildDrammenSykehusStaircaseDistanceMap(graph, adj, rooms) {
    // Build lookup: roomNumber → type
    const roomTypeMap = new Map(rooms.map((r) => [r.roomNumber, classifyDrammenSykehusRoom(r.roomName)]));
    // Fast lookup for nodes by ID to avoid O(N) searches in the Dijkstra loop
    const nodesById = new Map(graph.nodes.map((n) => [n.id, n]));
    // Staircase rooms
    const staircaseRooms = rooms.filter((r) => classifyDrammenSykehusRoom(r.roomName) === "staircase");
    // For each staircase, find centroid and closest node that belongs to it.
    const origins = [];
    for (const stair of staircaseRooms) {
        const pts = stair.polygon;
        const cx = pts.reduce((s, p) => s + p[0], 0) / pts.length;
        const cy = pts.reduce((s, p) => s + p[1], 0) / pts.length;
        let bestId = null;
        let bestDist = Infinity;
        for (const node of graph.nodes) {
            if (!(node.rooms ?? []).includes(stair.roomNumber))
                continue;
            const dx = node.posM[0] - cx;
            const dy = node.posM[1] - cy;
            const d = dx * dx + dy * dy;
            if (d < bestDist) {
                bestDist = d;
                bestId = node.id;
            }
        }
        if (bestId)
            origins.push({ nodeId: bestId, staircaseRoomNumber: stair.roomNumber });
    }
    const heap = [];
    const heapPush = (e) => {
        heap.push(e);
        let i = heap.length - 1;
        while (i > 0) {
            const parent = (i - 1) >> 1;
            if (heap[parent][0] <= heap[i][0])
                break;
            [heap[parent], heap[i]] = [heap[i], heap[parent]];
            i = parent;
        }
    };
    const heapPop = () => {
        if (heap.length === 0)
            return undefined;
        const top = heap[0];
        const last = heap.pop();
        if (heap.length > 0) {
            heap[0] = last;
            let i = 0;
            while (true) {
                const l = 2 * i + 1, r = 2 * i + 2;
                let s = i;
                if (l < heap.length && heap[l][0] < heap[s][0])
                    s = l;
                if (r < heap.length && heap[r][0] < heap[s][0])
                    s = r;
                if (s === i)
                    break;
                [heap[i], heap[s]] = [heap[s], heap[i]];
                i = s;
            }
        }
        return top;
    };
    // Track which staircase has claimed each corridor room.
    // The first staircase to reach any node inside a corridor claims it exclusively.
    const claimedCorridors = new Map(); // corridorRoomNumber → staircaseRoomNumber
    const getNodeCorridors = (nodeId) => (nodesById.get(nodeId)?.rooms ?? []).filter((r) => roomTypeMap.get(r) === "corridor");
    // Separate tracking: penaltyCost for heap ordering, euclidDist for stored distance.
    const penaltyCost = new Map();
    const dist = new Map();
    for (const { nodeId, staircaseRoomNumber } of origins) {
        penaltyCost.set(nodeId, 0);
        dist.set(nodeId, { dist: 0, staircaseRoomNumber });
        heapPush([0, nodeId, staircaseRoomNumber, 0]);
    }
    while (heap.length > 0) {
        const [d, nodeId, stairId, euclidSoFar] = heapPop();
        if (d > (penaltyCost.get(nodeId) ?? Infinity))
            continue;
        // Claim any corridor rooms on this node for this staircase (first arrival wins).
        for (const corridorId of getNodeCorridors(nodeId)) {
            if (!claimedCorridors.has(corridorId))
                claimedCorridors.set(corridorId, stairId);
        }
        for (const { neighbor, cost, distM } of adj.get(nodeId) ?? []) {
            // Block expansion into a corridor already claimed by a different staircase.
            const neighborCorridors = getNodeCorridors(neighbor);
            const blockedByClaim = neighborCorridors.some((cId) => {
                const owner = claimedCorridors.get(cId);
                return owner !== undefined && owner !== stairId;
            });
            if (blockedByClaim)
                continue;
            const nextCost = d + cost;
            if (nextCost < (penaltyCost.get(neighbor) ?? Infinity)) {
                const nextEuclid = euclidSoFar + distM;
                penaltyCost.set(neighbor, nextCost);
                dist.set(neighbor, { dist: nextEuclid, staircaseRoomNumber: stairId });
                heapPush([nextCost, neighbor, stairId, nextEuclid]);
            }
        }
    }
    return { distMap: dist, penaltyCostMap: penaltyCost };
}
// ---------------------------------------------------------------------------
// Room number assignment
// ---------------------------------------------------------------------------
const STAIRCASE_LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
/**
 * Determines the cardinal direction of a room relative to the floor centroid.
 */
function getCardinalDirection(roomCentroid, floorCentroid) {
    const dx = roomCentroid[0] - floorCentroid[0];
    const dy = roomCentroid[1] - floorCentroid[1];
    if (Math.abs(dx) > Math.abs(dy)) {
        return dx > 0 ? "E" : "W";
    }
    return dy > 0 ? "N" : "S";
}
/**
 * Traces backward from a node through the distance map toward the staircase.
 * Returns the path as an array of nodeIds from `startId` toward the staircase.
 */
function traceToStaircase(startId, adj, penaltyCostMap) {
    const path = [startId];
    const visited = new Set([startId]);
    let current = startId;
    const epsilon = 1e-9;
    while (true) {
        const neighbors = adj.get(current) ?? [];
        let bestId = null;
        let minNeighborCost = Infinity;
        const currentCost = penaltyCostMap.get(current) ?? Infinity;
        for (const { neighbor, cost: edgeCost } of neighbors) {
            if (visited.has(neighbor))
                continue;
            const d = penaltyCostMap.get(neighbor) ?? Infinity;
            // Only follow edges where the cost difference matches the edge cost exactly.
            // This prevents the path from jumping through walls or taking shortcuts that
            // were never part of the Dijkstra solution.
            if (Math.abs(d - (currentCost - edgeCost)) > epsilon)
                continue;
            if (d < minNeighborCost) {
                minNeighborCost = d;
                bestId = neighbor;
            }
        }
        // Stop if no neighbor is strictly closer (in penalty cost) to the staircase.
        if (bestId === null || minNeighborCost > currentCost + epsilon)
            break;
        path.push(bestId);
        visited.add(bestId);
        current = bestId;
        if (minNeighborCost < epsilon)
            break; // Reached a staircase origin
    }
    return path;
}
/**
 * Determines which side of the corridor path the room centroid is on using inverse torque.
 */
function detectRoomSide(roomCentroid, torquePath, nodePos) {
    let totalTorque = 0;
    for (let i = 0; i < torquePath.length - 1; i++) {
        const p1 = nodePos.get(torquePath[i]);
        const p2 = nodePos.get(torquePath[i + 1]);
        const dx = p2[0] - p1[0];
        const dy = p2[1] - p1[1];
        const mid = [(p1[0] + p2[0]) / 2, (p1[1] + p2[1]) / 2];
        const mx = roomCentroid[0] - mid[0];
        const my = roomCentroid[1] - mid[1];
        const distFromMid = Math.sqrt(mx * mx + my * my);
        const weight = 1 / ((distFromMid + 2) * (distFromMid + 2));
        const cx = roomCentroid[0] - p1[0];
        const cy = roomCentroid[1] - p1[1];
        totalTorque += (dx * cy - dy * cx) * weight;
    }
    return totalTorque > 0 ? "right" : "left";
}
/**
 * Orders rooms by adjacency to create a natural walking sequence:
 * 1. Start with the closest unpicked room to the staircase.
 * 2. Pick the next closest adjacent unpicked room to the current one.
 * 3. If no adjacent unpicked rooms exist, restart from the next closest unpicked room globally.
 */
function orderRoomsByAdjacency(rooms, adjTable) {
    const remaining = new Set(rooms.map((r) => r.roomNumber));
    const roomMap = new Map(rooms.map((r) => [r.roomNumber, r]));
    const ordered = [];
    while (remaining.size > 0) {
        // Find the closest unpicked room to start (or restart) a chain
        let currentId;
        let minDist = Infinity;
        for (const id of remaining) {
            const d = roomMap.get(id).distToStaircase;
            if (d < minDist) {
                minDist = d;
                currentId = id;
            }
        }
        while (currentId) {
            remaining.delete(currentId);
            const currentResult = roomMap.get(currentId);
            ordered.push(currentResult);
            const neighbors = adjTable.get(currentId) || new Set();
            let nextId;
            let nextMinDist = Infinity;
            // Find the closest neighbor that hasn't been picked yet
            for (const neighborId of neighbors) {
                if (remaining.has(neighborId)) {
                    const neighborResult = roomMap.get(neighborId);
                    if (neighborResult && neighborResult.distToStaircase < nextMinDist) {
                        nextMinDist = neighborResult.distToStaircase;
                        nextId = neighborId;
                    }
                }
            }
            currentId = nextId;
        }
    }
    return ordered;
}
/**
 * Assigns calculated room numbers to all ordinary rooms.
 */
export function assignDrammenSykehusRoomNumbers(graph, adj, distMap, penaltyCostMap, rooms, doors, roomAdjTable, floorNumber = 0) {
    if (RESET_NAMES) {
        rooms.forEach((room) => {
            if (!room.roomName)
                room.roomName = "-";
        });
    }
    const roomTypeMap = new Map(rooms.map((r) => [r.roomNumber, classifyDrammenSykehusRoom(r.roomName)]));
    const roomAreaMap = new Map(rooms.map((r) => [r.roomNumber, polygonArea(r.polygon)]));
    const hierarchy = buildDrammenSykehusRoomHierarchy(graph, adj, distMap, penaltyCostMap, rooms, doors);
    const SMALL_CORRIDOR_AREA_M2 = 10;
    // A room gets a base number if it is a regular room or a numbered_corridor (< 10 m²)
    // whose direct parent in the hierarchy is a large corridor (>= 10 m²) or a staircase.
    // Rooms parented to another room or a numbered_corridor become sub-rooms (suffixed).
    const isEligible = (r) => {
        const type = roomTypeMap.get(r.roomNumber);
        if (type === "numbered_corridor" && (roomAreaMap.get(r.roomNumber) ?? 0) >= SMALL_CORRIDOR_AREA_M2)
            return false;
        if (type !== "room" && type !== "numbered_corridor")
            return false;
        const parentRoomNumber = hierarchy[r.roomNumber]?.parent_room;
        if (!parentRoomNumber)
            return false;
        const parentType = roomTypeMap.get(parentRoomNumber);
        const parentArea = roomAreaMap.get(parentRoomNumber) ?? 0;
        return parentType === "staircase" ||
            (parentType === "corridor" && parentArea >= SMALL_CORRIDOR_AREA_M2);
    };
    // Fast lookup for nodes by ID
    const nodesById = new Map(graph.nodes.map((n) => [n.id, n]));
    // nodeId → position
    const nodePos = new Map(graph.nodes.map((n) => [n.id, [n.posM[0], n.posM[1]]]));
    // Staircase rooms sorted by centroid projected onto the (1,1) axis → letter A, B, C...
    // Buildings are rarely oriented exactly along (1,1), so staircases are unlikely to have
    // near-equal projections — reducing the risk of ambiguous or floor-inconsistent ordering.
    const staircaseRooms = rooms
        .filter((r) => classifyDrammenSykehusRoom(r.roomName) === "staircase")
        .map((r) => { const c = polygonCentroid(r.polygon); return { r, proj: c[0] + c[1] }; })
        .sort((a, b) => a.proj - b.proj)
        .map(({ r }) => r);
    // Calculate the centroid of the entire floor (etage)
    const allCentroids = rooms.map((r) => polygonCentroid(r.polygon));
    const count = allCentroids.length || 1;
    const floorCentroid = [
        allCentroids.reduce((sum, c) => sum + (c[0] ?? 0), 0) / count,
        allCentroids.reduce((sum, c) => sum + (c[1] ?? 0), 0) / count,
    ];
    const staircaseLetter = new Map(staircaseRooms.map((r, i) => [r.roomNumber, STAIRCASE_LETTERS[i % 26] ?? "?"]));
    // For each ordinary room, compute result
    const results = [];
    for (const room of rooms) {
        if (!isEligible(room))
            continue;
        // Find node with lowest distMap value belonging to this room
        let bestNodeId = null;
        let bestDist = Infinity;
        for (const node of graph.nodes) {
            if (!(node.rooms ?? []).includes(room.roomNumber))
                continue;
            const d = distMap.get(node.id)?.dist ?? Infinity;
            if (d < bestDist) {
                bestDist = d;
                bestNodeId = node.id;
            }
        }
        const staircaseRoomNumber = bestNodeId ? distMap.get(bestNodeId)?.staircaseRoomNumber : null;
        if (bestNodeId === null || bestDist === Infinity) {
            if (DEBUG)
                console.log(`[RoomNumbering] SKIP ${room.roomNumber} (${room.roomName ?? "?"}) — no reachable node in distMap`);
            continue;
        }
        // All node distances for this room (debug)
        const roomNodeDists = graph.nodes
            .filter((n) => (n.rooms ?? []).includes(room.roomNumber))
            .map((n) => ({ id: n.id, dist: distMap.get(n.id)?.dist ?? Infinity }))
            .sort((a, b) => a.dist - b.dist);
        // Trace back to staircase
        const path = traceToStaircase(bestNodeId, adj, penaltyCostMap);
        if (path.length === 0)
            continue;
        // Identify the first corridor room encountered on the way to the staircase
        let targetCorridorId = null;
        for (let i = 0; i < path.length; i++) {
            const nodeRooms = nodesById.get(path[i])?.rooms ?? [];
            const corridor = nodeRooms.find((r) => roomTypeMap.get(r) === "corridor");
            if (corridor) {
                targetCorridorId = corridor;
                break;
            }
        }
        if (!targetCorridorId)
            continue;
        if (!staircaseRoomNumber)
            continue;
        // Find node in targetCorridorId with largest distance value associated with staircaseRoomNumber
        let startNodeId = null;
        let maxD = -1;
        for (const node of graph.nodes) {
            if (!(node.rooms ?? []).includes(targetCorridorId))
                continue;
            const entry = distMap.get(node.id);
            if (entry && entry.staircaseRoomNumber === staircaseRoomNumber && entry.dist !== Infinity && entry.dist > maxD) {
                maxD = entry.dist;
                startNodeId = node.id;
            }
        }
        if (!startNodeId)
            continue;
        // Trace from that "upstream" node all the way back to the staircase
        const torquePath = traceToStaircase(startNodeId, adj, penaltyCostMap);
        const centroid = polygonCentroid(room.polygon);
        if (torquePath.length < 2)
            continue;
        const side = detectRoomSide(centroid, torquePath, nodePos);
        if (DEBUG)
            console.log(`[RoomNumbering] ${room.roomNumber} (${room.roomName ?? "?"})` +
                ` → staircase: ${staircaseRoomNumber}, side: ${side}, dist: ${bestDist.toFixed(2)}, torquePath: ${torquePath.length}` +
                ` | chosen node: ${bestNodeId}` +
                ` | node dists: [${roomNodeDists.slice(0, 5).map((n) => `${n.id}=${n.dist.toFixed(1)}`).join(", ")}${roomNodeDists.length > 5 ? "…" : ""}]`);
        results.push({
            roomNumber: room.roomNumber,
            calculatedRoomNumber: "", // filled in below
            staircaseRoomNumber,
            side,
            distToStaircase: bestDist,
            centroid,
        });
    }
    // Group by staircase, assign numbers
    const output = {};
    // Assign the staircase letter to the staircase rooms themselves
    for (const stair of staircaseRooms) {
        const letter = staircaseLetter.get(stair.roomNumber);
        if (letter) {
            output[stair.roomNumber] = letter;
            if (DEBUG)
                console.log(`[RoomNumbering] ASSIGNED STAIRCASE ${stair.roomNumber} → ${letter}`);
        }
    }
    const byStaircase = new Map();
    for (const r of results) {
        if (!byStaircase.has(r.staircaseRoomNumber))
            byStaircase.set(r.staircaseRoomNumber, []);
        byStaircase.get(r.staircaseRoomNumber).push(r);
    }
    for (const [staircaseRoomNumber, roomResults] of byStaircase) {
        const letter = staircaseLetter.get(staircaseRoomNumber) ?? "?";
        const floorStr = floorNumber.toString().padStart(2, "0");
        /** Helper to get the N-th valid number (1-based) skipping those ending in 0 or 5 */
        const getValidNumber = (targetIndex, start, step) => {
            let count = 0;
            let current = start - step;
            while (count <= targetIndex) {
                current += step;
                const lastDigit = current % 10;
                if (lastDigit !== 0 && lastDigit !== 5) {
                    count++;
                }
            }
            return current;
        };
        const right = orderRoomsByAdjacency(roomResults.filter((r) => r.side === "right"), roomAdjTable);
        const left = orderRoomsByAdjacency(roomResults.filter((r) => r.side === "left"), roomAdjTable);
        right.forEach((r, i) => {
            const num = getValidNumber(i, 2, 2);
            const dir = getCardinalDirection(r.centroid, floorCentroid);
            const tag = `${letter}${floorStr}${dir}.${num.toString().padStart(3, "0")}`;
            output[r.roomNumber] = tag;
            if (DEBUG)
                console.log(`[RoomNumbering] ASSIGNED ${r.roomNumber} → ${tag} (right, dist=${r.distToStaircase.toFixed(2)})`);
        });
        left.forEach((r, i) => {
            const num = getValidNumber(i, 1, 2);
            const dir = getCardinalDirection(r.centroid, floorCentroid);
            const tag = `${letter}${floorStr}${dir}.${num.toString().padStart(3, "0")}`;
            output[r.roomNumber] = tag;
            if (DEBUG)
                console.log(`[RoomNumbering] ASSIGNED ${r.roomNumber} → ${tag} (left, dist=${r.distToStaircase.toFixed(2)})`);
        });
    }
    // Step 2: for each base-numbered room, BFS its sub-rooms and assign suffixed numbers.
    // Sub-rooms that are themselves eligible (got a base number) are skipped.
    // All other descendants share the base number with suffix A, B, C... (flat BFS order).
    const SUFFIX_LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const assignedByBase = Object.entries(output)
        .filter(([roomNumber]) => results.some((r) => r.roomNumber === roomNumber));
    for (const [baseRoomNumber, baseTag] of assignedByBase) {
        const queue = [...(hierarchy[baseRoomNumber]?.sub_rooms ?? [])];
        let suffixIdx = 0;
        const visited = new Set([baseRoomNumber]);
        while (queue.length > 0) {
            const current = queue.shift();
            if (visited.has(current))
                continue;
            visited.add(current);
            const entry = hierarchy[current];
            if (!entry)
                continue;
            if (output[current] !== undefined && output[current] !== "-") {
                // Already has a base number — don't suffix it, but still recurse into its sub-rooms
                // so deeper descendants of it aren't missed (they path via current, not baseRoomNumber)
                continue;
            }
            const suffix = SUFFIX_LETTERS[suffixIdx % 26] ?? "?";
            output[current] = `${baseTag}${suffix}`;
            suffixIdx++;
            for (const sub of entry.sub_rooms) {
                if (!visited.has(sub))
                    queue.push(sub);
            }
        }
    }
    if (RESET_NAMES) {
        for (const room of rooms) {
            if (output[room.roomNumber] === undefined) {
                output[room.roomNumber] = "-";
            }
        }
    }
    return output;
}
// ---------------------------------------------------------------------------
// Room hierarchy
// ---------------------------------------------------------------------------
/**
 * Builds a parent-child hierarchy for all rooms based on the Dijkstra paths
 * toward staircases.  For each room the "parent" is the first different room
 * encountered when walking the shortest-cost path toward the staircase.
 */
export function buildDrammenSykehusRoomHierarchy(graph, adj, distMap, penaltyCostMap, rooms, doors = []) {
    const nodesById = new Map(graph.nodes.map((n) => [n.id, n]));
    const nodePos = new Map(graph.nodes.map((n) => [n.id, [n.posM[0], n.posM[1]]]));
    const hierarchy = {};
    for (const room of rooms) {
        hierarchy[room.roomNumber] = {
            sub_rooms: [],
            parent_room: null,
            door_to_parent: false,
            size: polygonArea(room.polygon),
            center: polygonCentroid(room.polygon),
        };
    }
    for (const room of rooms) {
        // Find node with lowest distMap value belonging to this room.
        let bestNodeId = null;
        let bestDist = Infinity;
        for (const node of graph.nodes) {
            if (!(node.rooms ?? []).includes(room.roomNumber))
                continue;
            const d = distMap.get(node.id)?.dist ?? Infinity;
            if (d < bestDist) {
                bestDist = d;
                bestNodeId = node.id;
            }
        }
        if (bestNodeId === null || bestDist === Infinity)
            continue;
        // Trace path toward staircase and find the first node that leaves this room.
        const path = traceToStaircase(bestNodeId, adj, penaltyCostMap);
        let parentRoom = null;
        let viaEdgeA = null;
        let viaEdgeB = null;
        for (let i = 1; i < path.length; i++) {
            const nodeRooms = nodesById.get(path[i])?.rooms ?? [];
            const otherRoom = nodeRooms.find((r) => r !== room.roomNumber);
            if (otherRoom !== undefined) {
                parentRoom = otherRoom;
                viaEdgeA = nodePos.get(path[i - 1]) ?? null;
                viaEdgeB = nodePos.get(path[i]) ?? null;
                break;
            }
        }
        if (parentRoom === null || !hierarchy[parentRoom])
            continue;
        const viasDoor = viaEdgeA !== null &&
            viaEdgeB !== null &&
            doors.length > 0 &&
            doors.some((door) => edgeCrossesDrammenSykehusDoor(viaEdgeA, viaEdgeB, door));
        hierarchy[room.roomNumber].parent_room = parentRoom;
        hierarchy[room.roomNumber].door_to_parent = viasDoor;
        hierarchy[parentRoom].sub_rooms.push(room.roomNumber);
    }
    return hierarchy;
}
// ---------------------------------------------------------------------------
// Debug download
// ---------------------------------------------------------------------------
/**
 * Serialises a routing graph to JSON and triggers a browser download.
 * Call this from a thunk or component after buildDrammenSykehusRoomNumberingGrid().
 */
export function debugDownloadDrammenSykehusRoomNumberingGrid(grid, distMap, filename = "room-numbering-grid.json") {
    const annotatedGrid = distMap
        ? {
            ...grid,
            nodes: grid.nodes.map((n) => ({
                ...n,
                distToStaircase: distMap.get(n.id)?.dist ?? null,
            })),
        }
        : grid;
    const json = JSON.stringify(annotatedGrid, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}
